import {create} from 'zustand';
import {QUESTIONIDS} from '../types/question';

export type QuestionId = (typeof QUESTIONIDS)[keyof typeof QUESTIONIDS];
export type QuestionType = 'Question' | 'Manual';

interface QuestionStore {
  currentQuestion: QuestionId;
  questionHistory: string[];
  /*
   * @param newQuestionId
   * @param identifier - used for question that has separate flows diverging from the same question type
   */
  setQuestionHistory: (newQuestionId: QuestionId, identifier?: string) => void;
  setCurrentQuestion: (
    newQuestionId?: QuestionId,
    saveHistory?: boolean,
  ) => void;
  /**
   * @description - pop the last question from the history
   */
  popQuestionHistory: () => void;
  goBack: () => void;
  questionType: QuestionType;
  setQuestionType: (type: QuestionType) => void;
}

const useQuestionStore = create<QuestionStore>((set) => ({
  currentQuestion: QUESTIONIDS.questionLanding,
  questionHistory: [],
  goBack: () => {
    set((state) => {
      const questionHistory = [...state.questionHistory];
      const previousQuestion = questionHistory.pop();

      if (!previousQuestion) return {};
      const [previousQuestionId] =
        parseQuestionIdWithIdentifier(previousQuestion);
      const payload: Partial<QuestionStore> = {
        questionHistory,
        currentQuestion: previousQuestionId,
      };

      return payload;
    });
  },
  popQuestionHistory: () => {
    set((state) => {
      const questionHistory = [...state.questionHistory];
      questionHistory.pop();
      return {
        questionHistory,
      };
    });
  },
  setQuestionHistory: (newQuestionId, identifier) => {
    set((state) => {
      const questionHistory = [...state.questionHistory];
      const questionIdWithIdentifier = stringifyQuestionIdWithIdentifier(
        newQuestionId,
        identifier,
      );
      questionHistory.push(questionIdWithIdentifier);
      return {
        questionHistory,
      };
    });
  },
  setCurrentQuestion: (newQuestionId, saveHistory = true) => {
    set((state) => {
      if (!newQuestionId) return {};

      const payload: Partial<QuestionStore> = {
        currentQuestion: newQuestionId,
      };

      if (saveHistory) {
        const questionHistory = [...state.questionHistory];
        questionHistory.push(state.currentQuestion);
        payload.questionHistory = questionHistory;
      }
      return payload;
    });
  },
  questionType: 'Question',
  setQuestionType: (content: QuestionType) => {
    set(() => {
      // const questionHistory = [...state.questionHistory];
      return {
        // questionHistory,
        questionType: content,
      };
    });
  },
}));

export default useQuestionStore;

function stringifyQuestionIdWithIdentifier(
  questionId: QuestionId,
  identifier?: string,
) {
  if (!identifier) return questionId;
  return `${questionId}@@${identifier}`;
}

export function parseQuestionIdWithIdentifier(
  text: string,
): [QuestionId, string] {
  if (!text.includes('@@')) return [text as QuestionId, ''];
  const [questionId, identifier] = text.split('@@');
  return [questionId as QuestionId, identifier];
}
